/**
 * Editable Profiles "User guide" (staff portal).
 *
 * Staff maintain a single live HTML document that firms see read-only in the
 * customer portal (account-ui). Editing uses the design-system WYSIWYG editor
 * via the staff `RichTextField` adapter (`full` preset, HTML source tab). The
 * Save control is gated behind `profiles.user_guide.write`; staff without the
 * permission get a disabled, read-only editor.
 *
 * Data fetching follows the staff-ui Profiles convention (manual
 * `useState`/`useEffect` against `src/api/profiles/*` services) rather than
 * TanStack Query — see `LawyerProfiles/index.tsx`. The closest edit precedent
 * is CMS `PageDetailPage.tsx`.
 */
import { useEffect, useState } from "react";
import { Button, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader";
import LoadingState from "@/components/LoadingState";
import PermissionGate from "@/components/Permissions/PermissionGate";
import { RichTextField } from "@/components/Profiles/RichTextField";
import { userGuideService } from "@/api/profiles/userGuideService";
import styles from "../ProfilesList.module.css";

export function UserGuidePage() {
  const [content, setContent] = useState("");
  const [savedContent, setSavedContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const guide = await userGuideService.get();
        if (!cancelled) {
          setContent(guide.content);
          setSavedContent(guide.content);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load the user guide");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const isDirty = content !== savedContent;

  async function handleSave() {
    setSaving(true);
    try {
      const guide = await userGuideService.update(content);
      // Re-sync from the server response (it returns the sanitised HTML).
      setContent(guide.content);
      setSavedContent(guide.content);
      setToast({ message: "User guide saved successfully", type: "success" });
    } catch (err) {
      setToast({
        message: `Failed to save: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <LoadingState message="Loading user guide..." />;
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="User guide"
        subtitle="Guidance shown read-only to firms in the customer portal"
      >
        <PermissionGate permission="profiles.user_guide.write">
          <Button
            variant="contained"
            color="primary"
            disabled={saving || !isDirty}
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </PermissionGate>
      </PageHeader>

      <div className={styles.contentSection}>
        {error && (
          <div role="alert" className={styles.errorBanner}>
            {error}
          </div>
        )}

        <PermissionGate
          permission="profiles.user_guide.write"
          fallback={
            <RichTextField
              value={content}
              onChange={() => {
                /* read-only: no permission to edit */
              }}
              preset="full"
              disabled
              label="User guide"
              minHeight={400}
            />
          }
        >
          <RichTextField
            value={content}
            onChange={setContent}
            preset="full"
            htmlTab
            mediaEnabled
            allowImageUrl
            label="User guide"
            minHeight={400}
          />
        </PermissionGate>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default UserGuidePage;
